import { cliente } from "../http/cliente";
import type {
  AlertaHistorico,
  EmpresaAlertas,
  FiltrosAlertas,
  ReagendarResposta,
} from "./tipos";

/*
 * Fetchers do historico de alertas (FE-7 — Tela 7 §10.5).
 *
 * Camada fina sobre o `cliente` HTTP (CP-C): injeta Bearer, trata refresh/401/503
 * e normaliza erro em `ErroHttp`. Estes fetchers NAO tratam erro — propagam a
 * rejeicao do `cliente` para o TanStack Query (hooks em `./hooks`) decidir.
 *
 * `baseURL` do cliente ja e `/api`, entao os paths aqui sao relativos a ela.
 */

/**
 * Monta o objeto `params` da consulta do historico OMITINDO toda chave opcional
 * ausente ou vazia.
 *
 * `limit`/`offset` SEMPRE entram (numeros, default no backend). Os demais
 * (`empresa_id`/`severidade`/`canal`/`status`) so entram quando presentes e
 * nao-vazios — nunca enviamos `severidade=""` (o backend rejeitaria string vazia
 * onde espera enum/UUID). `undefined` tambem fica de fora.
 */
function montarParams(
  filtros: FiltrosAlertas,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    limit: filtros.limit,
    offset: filtros.offset,
  };
  if (filtros.empresa_id) params.empresa_id = filtros.empresa_id;
  if (filtros.severidade) params.severidade = filtros.severidade;
  if (filtros.canal) params.canal = filtros.canal;
  if (filtros.status) params.status = filtros.status;
  return params;
}

/** `GET /api/alertas` — historico de alertas por canal/status (array puro). */
export async function buscarAlertas(
  filtros: FiltrosAlertas,
): Promise<AlertaHistorico[]> {
  const resposta = await cliente.get<AlertaHistorico[]>("/alertas", {
    params: montarParams(filtros),
  });
  return resposta.data;
}

/** `GET /api/alertas/empresas` — empresas configuraveis pelo escopo do usuario. */
export async function buscarEmpresasAlertas(): Promise<EmpresaAlertas[]> {
  const resposta = await cliente.get<EmpresaAlertas[]>("/alertas/empresas");
  return resposta.data;
}

/**
 * `POST /api/outbox/{outboxId}/reagendar` — reabre a notificacao para novo envio.
 *
 * `outboxId` e o `id_outbox` da linha do historico. `justificativa` deve ter >= 10
 * caracteres apos `.trim()` (o backend rejeita com 422 abaixo disso).
 */
export async function reagendarNotificacao(
  outboxId: string,
  corpo: { justificativa: string },
): Promise<ReagendarResposta> {
  const resposta = await cliente.post<ReagendarResposta>(
    `/outbox/${outboxId}/reagendar`,
    corpo,
  );
  return resposta.data;
}
